import { Journey } from '@audibene/ta.utils-opportunity'
import { endOfDay } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useEffect, useMemo, useState } from 'react'
import { AppointmentType } from '../../constants'
import { AvailabilitiesResponse, Resource } from '../../types'
import { mergeAsegoTimes } from '../../util/mergeAsegoTimes'
import { startOfDayTimezoned } from '../../util'
import { useAsegoAvailabilityTimes } from '../useAsegoAvailabilityTimes'
import { useDateFormat } from '../useDateFormat'

interface UseAsegoTimesIntegrationParams {
  appointmentType: AppointmentType
  dateEnd: Date
  dateStart: Date
  durationInMinutes?: number
  journey: Journey
  resources: Resource[]

  timeZone: string
  clientStartDate: Date | undefined
  asegoAvailabilities: AvailabilitiesResponse | undefined
  mergedAvailabilityData: AvailabilitiesResponse | undefined
}

const useAsegoTimesIntegration = ({
  appointmentType,
  durationInMinutes,
  journey,
  resources,
  timeZone,
  clientStartDate,
  asegoAvailabilities,
  mergedAvailabilityData,
}: UseAsegoTimesIntegrationParams) => {
  const { dateToISODateKey } = useDateFormat(timeZone)
  const resourceGids = useMemo(() => resources.map((resource) => resource.gid), [resources])

  const clickedDateKey = clientStartDate ? dateToISODateKey(clientStartDate) : undefined

  const hasAsegoAvailabilityForDate = (() => {
    if (!clientStartDate || !asegoAvailabilities || !clickedDateKey) return false
    return Boolean(asegoAvailabilities.dayAvailability[clickedDateKey])
  })()

  const isAsegoTimesQueryEnabled = Boolean(durationInMinutes && clientStartDate && hasAsegoAvailabilityForDate)

  const {
    data: asegoTimes,
    isLoading: isAsegoTimesLoading,
    isError: isAsegoTimesError,
  } = useAsegoAvailabilityTimes({
    appointmentType,
    dateEnd: zonedTimeToUtc(endOfDay(clientStartDate || new Date()), timeZone),
    dateStart: startOfDayTimezoned(clientStartDate || new Date(), timeZone),
    durationInMinutes,
    isEnabled: isAsegoTimesQueryEnabled,
    journey,
    resourceGids: resourceGids,
    timeZone,
  })

  // Track dates where /times confirmed no slots exist, so they stay red across re-renders.
  const [confirmedEmptyDates, setConfirmedEmptyDates] = useState<Set<string>>(new Set())

  useEffect(() => {
    setConfirmedEmptyDates(new Set())
  }, [asegoAvailabilities])

  useEffect(() => {
    if (isAsegoTimesLoading || isAsegoTimesError || !clickedDateKey) return

    const dayResources = mergedAvailabilityData?.dayAvailability?.[clickedDateKey]
    if (!dayResources) return

    const hasEmptyMarkers = Object.values(dayResources).some((slots) => Array.isArray(slots) && slots.length === 0)
    if (hasEmptyMarkers) {
      setConfirmedEmptyDates((prev) => {
        if (prev.has(clickedDateKey)) return prev
        const next = new Set(prev)
        next.add(clickedDateKey)
        return next
      })
    }
  }, [isAsegoTimesLoading, isAsegoTimesError, clickedDateKey, mergedAvailabilityData])

  const finalMergedData = useMemo(() => {
    const merged = mergeAsegoTimes(mergedAvailabilityData, asegoTimes)
    if (!merged) return merged

    const datesToClean = new Set(confirmedEmptyDates)
    if (!isAsegoTimesLoading && !isAsegoTimesError && clickedDateKey) {
      datesToClean.add(clickedDateKey)
    }

    if (datesToClean.size === 0) return merged

    const cleanedDayAvailability = { ...merged.dayAvailability }

    for (const dateKey of datesToClean) {
      const dayResources = cleanedDayAvailability[dateKey]
      if (!dayResources) continue

      const cleaned = { ...dayResources }
      let changed = false
      for (const [resourceId, slots] of Object.entries(cleaned)) {
        if (Array.isArray(slots) && slots.length === 0) {
          delete cleaned[resourceId]
          changed = true
        }
      }

      if (!changed) continue

      if (Object.keys(cleaned).length === 0) {
        delete cleanedDayAvailability[dateKey]
      } else {
        cleanedDayAvailability[dateKey] = cleaned
      }
    }

    return { ...merged, dayAvailability: cleanedDayAvailability }
  }, [mergedAvailabilityData, asegoTimes, confirmedEmptyDates, isAsegoTimesLoading, isAsegoTimesError, clickedDateKey])

  return {
    mergedData: finalMergedData,
    isLoading: isAsegoTimesLoading,
    isError: isAsegoTimesError,
  }
}

export { useAsegoTimesIntegration }
